import React, { useCallback, useMemo, useState } from "react";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { Home } from "lucide-react";
import { AerialComponent, FormComponentProps } from "../DynamicFormTypes.ts";
import { useFormMethods, useFormStatic } from "../DyanmicFormContext.tsx";
import { GoogleMap } from "../../google/GoogleMap.tsx";

type InlineField = {
    label: string;
    value?: string | number | null;
    suffix?: string;
};

const InlineFields: React.FC<{ fields: InlineField[] }> = ({ fields }) => {
    const validFields = fields.filter(
        (f) => f.value !== undefined && f.value !== null && f.value !== ""
    );

    if (validFields.length === 0) return null;

    return (
        <div>
            {validFields.map((field, index) => (
                <React.Fragment key={field.label}>
                    {index > 0 && " | "}
                    {field.label}: {field.value}
                    {field.suffix ?? ""}
                </React.Fragment>
            ))}
        </div>
    );
};

type SubjectDetails = {
    rooms?: string;
    beds?: string;
    baths?: string;
    glaSqft?: string;
    lotAcres?: string;
    yearBuilt?: string;
};

const SUBJECT_FIELDS = [
    { key: "subjectPropertyRooms", prop: "rooms" },
    { key: "subjectPropertyBeds", prop: "beds" },
    { key: "subjectPropertyBaths", prop: "baths" },
    { key: "subjectPropertyGla", prop: "glaSqft" },
    { key: "subjectPropertyLand", prop: "lotAcres" },
    { key: "subjectPropertyYearBuilt", prop: "yearBuilt" },
] as const;

const SUBJECT_KEYS = SUBJECT_FIELDS.map((f) => f.key);

type SubjectProp = (typeof SUBJECT_FIELDS)[number]["prop"];

const TOOLTIP_STYLE: React.CSSProperties = {
    position: "absolute",
    bottom: "50px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "white",
    padding: "12px",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
    whiteSpace: "nowrap",
    zIndex: 1,
    minWidth: "250px",
    pointerEvents: "none",
};

const TITLE_STYLE: React.CSSProperties = { fontWeight: "bold", marginBottom: "6px" };

export const AerialMapRenderer: React.FC<FormComponentProps<AerialComponent>> = ({ component }) => {
    console.log("Rendering AerialMap with component: ", component);

    const { metaData } = useFormStatic();
    const { formMethods } = useFormMethods();

    const lat = metaData?.latitude != null ? Number(metaData.latitude) : null;
    const lng = metaData?.longitude != null ? Number(metaData.longitude) : null;

    const center = useMemo(() => {
        if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return null;
        return { lat, lng };
    }, [lat, lng]);

    const [showDetails, setShowDetails] = useState(false);
    const [subjectDetails, setSubjectDetails] = useState<SubjectDetails | null>(null);

    const getSubjectDetailsObject = useCallback((): SubjectDetails => {
        const values = formMethods.getValues(SUBJECT_KEYS) as unknown as Array<string | undefined>;

        const details: Partial<Record<SubjectProp, string | undefined>> = {};
        SUBJECT_FIELDS.forEach((field, idx) => {
            details[field.prop] = values[idx];
        });

        return details as SubjectDetails;
    }, [formMethods]);

    const showSubjectDetails = useCallback(() => {
        setShowDetails(true);
        setSubjectDetails(getSubjectDetailsObject());
    }, [getSubjectDetailsObject]);

    const hideSubjectDetails = useCallback(() => {
        setShowDetails(false);
    }, []);

    if (!center) return null;

    return (
        <GoogleMap lat={center.lat} lng={center.lng} defaultZoom={18}>
            <AdvancedMarker position={center} onMouseEnter={showSubjectDetails} onMouseLeave={hideSubjectDetails}>
                <Home size={28} color="#4285F4" fill="#4285F4" />

                {showDetails && (
                    <div style={TOOLTIP_STYLE}>
                        <div style={TITLE_STYLE}>SUBJECT PROPERTY</div>

                        {metaData?.streetAddress && <div>{metaData.streetAddress}</div>}

                        <InlineFields
                            fields={[
                                { label: "Rooms", value: subjectDetails?.rooms },
                                { label: "Bed", value: subjectDetails?.beds },
                                { label: "Bath", value: subjectDetails?.baths },
                            ]}
                        />

                        <InlineFields
                            fields={[
                                { label: "GLA", value: subjectDetails?.glaSqft, suffix: " sqft." },
                                { label: "Lot", value: subjectDetails?.lotAcres, suffix: " acres." },
                                { label: "Built", value: subjectDetails?.yearBuilt },
                            ]}
                        />
                    </div>
                )}
            </AdvancedMarker>
        </GoogleMap>
    );
};

export const AerialMap = React.memo(AerialMapRenderer);